import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FIELD = "h-10 border-forest-700/20 bg-white";
const EMPTY = { question: "", answer: "", sort_order: 0, active: true };

export default function AdminFaqs() {
  const [faqs, setFaqs] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/faqs").then((d) => setFaqs(d.data)).catch((e) => {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
    setFaqs([]);
  });

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (f) => {
    setEditId(f.id);
    setForm({ question: f.question, answer: f.answer, sort_order: f.sort_order || 0, active: !!f.active });
    setOpen(true);
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.question || !form.answer) {
      toast.error("Question and answer are required");
      return;
    }
    setSaving(true);
    const payload = { question: form.question, answer: form.answer, sort_order: Number(form.sort_order) || 0, active: form.active };
    try {
      if (editId) await api.put(`/admin/faqs/${editId}`, payload);
      else await api.post("/admin/faqs", payload);
      setOpen(false);
      toast.success(editId ? "FAQ updated" : "FAQ added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/faqs/${deleteTarget.id}`);
      toast.success("FAQ deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div data-testid="admin-faqs">
      <div className="mb-4 flex items-center gap-3">
        <span className="text-xs font-bold text-moss" data-testid="faqs-count">
          {faqs === null ? "Loading…" : `${faqs.length} question${faqs.length === 1 ? "" : "s"}`}
        </span>
        <Button onClick={openNew} data-testid="admin-add-faq" className="ml-auto gap-2 rounded-full bg-terra hover:bg-terra-dark">
          <Plus className="h-4 w-4" /> Add FAQ
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-forest-700/10 bg-white">
        <Table data-testid="faqs-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">Order</TableHead>
              <TableHead>Question</TableHead>
              <TableHead className="w-20">Live</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {faqs === null ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-forest-700" />
                </TableCell>
              </TableRow>
            ) : faqs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-sm text-moss" data-testid="faqs-empty">
                  No FAQs yet — add the first one.
                </TableCell>
              </TableRow>
            ) : (
              faqs.map((f) => (
                <TableRow key={f.id} data-testid={`faq-row-${f.id}`}>
                  <TableCell className="text-moss">{f.sort_order}</TableCell>
                  <TableCell className="max-w-md truncate font-semibold text-forest-700">{f.question}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-bold ${f.active ? "text-forest-600" : "text-red-500"}`}>
                      {f.active ? "Live" : "Hidden"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="icon" className="h-8 w-8 border-forest-700/20" onClick={() => openEdit(f)} data-testid={`faq-edit-${f.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8 border-red-200 text-red-500" onClick={() => setDeleteTarget(f)} data-testid={`faq-delete-${f.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-xl" data-testid="faq-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-forest-700">{editId ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Question *</Label>
              <Input className={FIELD} value={form.question} onChange={(e) => set("question", e.target.value)} data-testid="faq-form-question" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Answer *</Label>
              <Textarea rows={4} value={form.answer} onChange={(e) => set("answer", e.target.value)} data-testid="faq-form-answer" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Sort order</Label>
                <Input type="number" className={FIELD} value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} data-testid="faq-form-sort" />
              </div>
              <label className="flex items-end gap-2.5 pb-2 text-sm text-forest-700">
                <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="faq-form-active" />
                Live on public site
              </label>
            </div>
            <div className="flex justify-end gap-3 border-t border-forest-700/10 pt-4">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="faq-form-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="gap-2 rounded-full bg-forest-700 hover:bg-forest-600" data-testid="faq-form-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editId ? "Save changes" : "Add FAQ"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="faq-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this FAQ?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.question}" will be removed from the public FAQ page immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="faq-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="faq-delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
